import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";

export default function JobOrderNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [accounts, setAccounts] = useState<{ id: string; name: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    clientAccountId: "",
    title: "",
    skillsRequired: "",
    visaRequirement: "",
    locationType: "REMOTE",
    location: "",
    billRate: "",
    payRate: "",
    status: "OPEN",
    entity: "BOTH",
    isGovernment: false,
    clearanceLevel: "",
  });

  useEffect(() => {
    api.get("/accounts", { params: { pageSize: 100, sortBy: "name", sortDir: "asc" } })
      .then((r) => setAccounts(r.data.data ?? []));
  }, []);

  function set<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const payload: any = {
        clientAccountId: form.clientAccountId,
        title: form.title,
        skillsRequired: form.skillsRequired.split(",").map((s) => s.trim()).filter(Boolean),
        locationType: form.locationType,
        status: form.status,
        entity: form.entity,
        isGovernment: form.isGovernment,
      };
      if (form.visaRequirement) payload.visaRequirement = form.visaRequirement;
      if (form.location)        payload.location = form.location;
      if (form.billRate)        payload.billRate = Number(form.billRate);
      if (form.payRate)         payload.payRate = Number(form.payRate);
      if (form.clearanceLevel)  payload.clearanceLevel = form.clearanceLevel;
      await api.post("/job-orders", payload);
      qc.invalidateQueries({ queryKey: ["/job-orders"] });
      navigate("/job-orders");
    } catch (err: any) {
      setError(err?.response?.data?.message ?? "Failed to create job order.");
    } finally { setBusy(false); }
  }

  return (
    <>
      <PageHeader title="New Job Order" description="Create a client requisition." />
      <form onSubmit={submit} className="p-6">
        <Card className="max-w-2xl">
          <CardContent className="grid gap-4 p-6 md:grid-cols-2">
            <div className="md:col-span-2">
              <Label htmlFor="title">Title *</Label>
              <Input id="title" required value={form.title} onChange={(e) => set("title", e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="clientAccountId">Client *</Label>
              <Select id="clientAccountId" required value={form.clientAccountId} onChange={(e) => set("clientAccountId", e.target.value)}>
                <option value="">— Select client account —</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="skillsRequired">Skills (comma-separated)</Label>
              <Input id="skillsRequired" placeholder="React, Node, AWS" value={form.skillsRequired} onChange={(e) => set("skillsRequired", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="locationType">Location type</Label>
              <Select id="locationType" value={form.locationType} onChange={(e) => set("locationType", e.target.value)}>
                <option value="REMOTE">Remote</option>
                <option value="HYBRID">Hybrid</option>
                <option value="ONSITE">Onsite</option>
              </Select>
            </div>
            <div>
              <Label htmlFor="location">Location</Label>
              <Input id="location" placeholder="NYC, Bangalore, …" value={form.location} onChange={(e) => set("location", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="billRate">Bill rate ($/hr)</Label>
              <Input id="billRate" type="number" min="0" step="0.01" value={form.billRate} onChange={(e) => set("billRate", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="payRate">Pay rate ($/hr)</Label>
              <Input id="payRate" type="number" min="0" step="0.01" value={form.payRate} onChange={(e) => set("payRate", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="visaRequirement">Visa requirement</Label>
              <Input id="visaRequirement" placeholder="USC/GC only, any visa, …" value={form.visaRequirement} onChange={(e) => set("visaRequirement", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="entity">Entity</Label>
              <Select id="entity" value={form.entity} onChange={(e) => set("entity", e.target.value)}>
                <option value="BOTH">Both</option>
                <option value="US">US</option>
                <option value="INDIA">India</option>
                <option value="GLOCALIZE">Glocalize</option>
              </Select>
            </div>
            <div>
              <Label htmlFor="status">Status</Label>
              <Select id="status" value={form.status} onChange={(e) => set("status", e.target.value)}>
                <option value="OPEN">Open</option>
                <option value="HOLD">Hold</option>
                <option value="FILLED">Filled</option>
                <option value="CANCELLED">Cancelled</option>
              </Select>
            </div>
            <div className="md:col-span-2 flex items-center gap-2">
              <input id="isGovernment" type="checkbox" checked={form.isGovernment} onChange={(e) => set("isGovernment", e.target.checked)} />
              <Label htmlFor="isGovernment">Government / cleared role</Label>
            </div>
            {form.isGovernment && (
              <div className="md:col-span-2">
                <Label htmlFor="clearanceLevel">Clearance level</Label>
                <Input id="clearanceLevel" placeholder="Public Trust, Secret, TS/SCI, …" value={form.clearanceLevel} onChange={(e) => set("clearanceLevel", e.target.value)} />
              </div>
            )}
          </CardContent>
          <div className="flex items-center justify-between border-t px-6 py-4">
            {error ? <p className="text-sm text-destructive">{error}</p> : <span />}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => navigate("/job-orders")}>Cancel</Button>
              <Button type="submit" disabled={busy || !form.title || !form.clientAccountId}>{busy ? "Saving…" : "Create"}</Button>
            </div>
          </div>
        </Card>
      </form>
    </>
  );
}
